import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="printer"
export default class extends Controller {
  static targets = ["item", "toggleButton", "barcodeOnly", "rangeStart", "rangeEnd"]

  connect() {
    this.port = null // 포트를 저장하기 위한 클래스 속성
  }

  get isBarcodeOnly() {
    return this.hasBarcodeOnlyTarget && this.barcodeOnlyTarget.checked
  }

  togglePrintButtons(enable) {
    const printButtons = document.querySelectorAll('button.print-button')

    printButtons.forEach((button) => {
      button.disabled = !enable
      if (enable) {
        button.classList.remove('bg-danger')
      } else {
        button.classList.add('bg-danger')
      }
    })

    if (this.hasBarcodeOnlyTarget) {
      this.barcodeOnlyTarget.disabled = !enable
    }
  }

  printerReset(event) {
    event.preventDefault()

    localStorage.removeItem('RFIDSerialPortIndex')
    localStorage.removeItem('receiptSerialPortIndex')

    this.togglePrintButtons(true)

    alert('프린터 포트를 초기화 했습니다.')
    window.location.reload()
  }

  selectAll(event) {
    event.preventDefault()

    const isSelectAll = this.toggleButtonTarget.textContent.trim() === "전체선택"

    this.itemTargets.forEach((checkbox) => {
      checkbox.checked = isSelectAll
    })

    this.toggleButtonTarget.textContent = isSelectAll ? "전체해제" : "전체선택"

    if (isSelectAll) {
      this.toggleButtonTarget.classList.add("btn", "bg-danger", "text-white")
      this.toggleButtonTarget.classList.remove("bg-primary", "text-white")
    } else {
      this.toggleButtonTarget.classList.add("btn", "bg-primary", "text-white")
      this.toggleButtonTarget.classList.remove("bg-danger", "text-white")
    }
  }

  selectRange(event) {
    event.preventDefault()

    const start = Number.parseInt(this.rangeStartTarget.value, 10)
    const end = Number.parseInt(this.rangeEndTarget.value, 10)

    if (!Number.isInteger(start) || !Number.isInteger(end)) {
      alert("시작번호와 끝번호를 입력해주세요.")
      return
    }

    const rangeStart = Math.min(start, end)
    const rangeEnd = Math.max(start, end)

    if (rangeStart < 1 || rangeEnd > this.itemTargets.length) {
      alert(`선택 범위는 1부터 ${this.itemTargets.length}까지 입력해주세요.`)
      return
    }

    this.itemTargets.forEach((checkbox, index) => {
      const position = index + 1
      checkbox.checked = position >= rangeStart && position <= rangeEnd
    })

    this.toggleButtonTarget.textContent = "전체선택"
    this.toggleButtonTarget.classList.add("btn", "bg-primary", "text-white")
    this.toggleButtonTarget.classList.remove("bg-danger")

    if (rangeStart === 1 && rangeEnd === this.itemTargets.length) {
      this.toggleButtonTarget.textContent = "전체해제"
      this.toggleButtonTarget.classList.add("btn", "bg-danger", "text-white")
      this.toggleButtonTarget.classList.remove("bg-primary")
    }
  }

  async openPort() {
    // 이전에 선택한 포트를 우선 사용 (localStorage에서 포트 인덱스를 가져옴)
    const previousPortIndex = localStorage.getItem('RFIDSerialPortIndex')

    const getPort = async () => {
      const ports = await navigator.serial.getPorts()

      if (previousPortIndex && ports[previousPortIndex]) {
        console.log(`기존 프린터 포트 사용:`, ports[previousPortIndex])
        return ports[previousPortIndex]
      } else {
        console.log("새 포트를 선택해야 합니다.")
        return this.requestAndSavePort()
      }
    }

    const port = await getPort()
    console.log('선택된 프린터 포트:', port)

    if (!port) {
      throw new Error('포트가 선택되지 않았습니다.')
    }

    // 선택한 포트를 localStorage에 저장 (다음 사용을 위해)
    const ports = await navigator.serial.getPorts()
    const selectedIndex = ports.indexOf(port)
    localStorage.setItem('RFIDSerialPortIndex', selectedIndex)

    if (!port.readable || !port.writable) {
      await port.open({
        baudRate: 9600, dataBits: 8, stopBits: 1, parity: 'none', flowControl: 'none'
      })
    }

    return port
  }

  async requestAndSavePort() {
    const port = await navigator.serial.requestPort()
    const ports = await navigator.serial.getPorts()

    // 사용자가 선택한 포트의 인덱스를 저장
    const selectedIndex = ports.indexOf(port)
    localStorage.setItem('RFIDSerialPortIndex', selectedIndex)

    console.log(`선택된 포트:`, port)
    return port
  }

  async closePort() {
    if (this.port) {
      try {
        await this.port.close()
        console.log("포트 닫음.")
      } catch (err) {
        console.log("포트 닫는중 에러 발생:", err)
      }
      this.port = null
    }
  }

  async print(event) {
    let port = this.port

    const index = event.currentTarget.dataset.printerIndex
    const epc = event.currentTarget.dataset.printerEpc
    const itemName = event.currentTarget.dataset.printerItemName
    const nickName = event.currentTarget.dataset.printerNickName
    const basePrice = event.currentTarget.dataset.printerBasePrice
    const repotPrice = event.currentTarget.dataset.printerRepotPrice || ""
    const itemCode = event.currentTarget.dataset.printerItemCode
    const serial = event.currentTarget.dataset.printerSerial

    const epcField = 11
    const checkboxField = 1
    const statusField = 4
    const countField = 7

    const outputTarget = this.element.querySelector(`[data-printer-target="output-${index}"]`)
    outputTarget.innerHTML = "프린팅"

    // 가져온 데이터를 dataObj에 저장
    const dataObj = {
      epc: epc, item_name: itemName, nick_name: nickName, base_price: basePrice, repot_price: repotPrice, item_code: itemCode, serial: serial
    }

    try {
      if (!port) {
        // 포트가 열려있지 않으면 열기
        port = await this.openPort()
        this.port = port
      }

      const today = new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '-').replace('.', '')

      const barcodeOnly = this.isBarcodeOnly

      const rfidBlock = `
        RFWTAG;128;EPC
        128;H;"${dataObj.epc}"
        STOP
        RFRTAG;128;EPC
        128;DF2;H
        STOP
        VERIFY;DF2;H;**;**`

      let shift = 0
      if (barcodeOnly === true) {
      shift = 1.5
      }

      const printCommand = `
        ~CREATE;LABEL1;X
        BARCODE
        QRCODE;X1;Y1;E2;DARK;${(4.5 - shift).toFixed(1)};14;
        *${dataObj.epc}*
        STOP
        FONT;SYMSET 'UTF8'
        FONT;NAME K4PTX.TTF
        ALPHA
        POINT;${(3.5 - shift).toFixed(1)};2;12;8;*${dataObj.item_name}*
        POINT;${(5.0 - shift).toFixed(1)};2;12;8;*${dataObj.nick_name}*
        POINT;${(6.2 - shift).toFixed(1)};2;12;8;*${dataObj.repot_price}*
        POINT;${(7.8 - shift).toFixed(1)};2;16;12;*${dataObj.base_price}*
        ${(8.6 - shift).toFixed(1)};13;1;1;*${dataObj.serial.toString().padStart(8, '0')}*
        POINT;${(7.6 - shift).toFixed(1)};13;12;8;*${today}*
        STOP${barcodeOnly ? "" : rfidBlock}
        END
        ~EXECUTE;LABEL1;1
        ~NORMAL
      `

      // Get a writer
      const writer = port.writable.getWriter()
      // Encode and send the data
      const encoder = new TextEncoder()
      await writer.write(encoder.encode(printCommand))
      writer.releaseLock()

      const targetRow = document.getElementById(index)

      const markSuccess = async () => {
        // Epc status 업데이트 (RFID/바코드 모드 공통: status, is_printed, print_count 모두 갱신)
        await fetch("/orders/print_epc", {
          method: "POST", headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          }, body: JSON.stringify({epc: dataObj.epc})
        })
        outputTarget.textContent = "성공"

        const checkValue = targetRow.querySelectorAll("td")[checkboxField]
        const checkbox = checkValue.querySelector('input[type="checkbox"]')
        if (checkbox) {
          checkbox.checked = false
        }

        const countTd = targetRow.querySelectorAll("td")[countField]
        let countValue = parseInt(countTd.textContent) || 0
        countValue += 1
        countTd.textContent = countValue

        const statusTd = targetRow.querySelectorAll("td")[statusField]
        statusTd.textContent = "true"
      }

      if (barcodeOnly) {
        // 바코드 전용: 시리얼 응답 읽기 / EPC 검증 스킵
        await markSuccess()
        await new Promise(r => setTimeout(r, 2000)); // 2초 대기
      } else {
        // Get a reader
        const reader = port.readable.getReader()
        const decoder = new TextDecoder()
        let response = ""

        while (true) {
          const {value, done} = await reader.read()
          if (done) {
            break
          }
          response += decoder.decode(value)
          if (response.includes("\x00")) {
            break
          }
        }
        reader.releaseLock()

        // Process response
        console.log("프린터 응답: ", response)

        // EPC td 값을 추출하여 응답과 비교
        const epcTdValue = targetRow.querySelectorAll("td")[epcField].textContent.trim()

        // 응답과 기대 값을 모두 trim()으로 공백 제거 후 비교
        const cleanedResponse = response.trim().replace(/[^a-zA-Z0-9]/g, '')
        const cleanedSecondTdValue = epcTdValue.trim().replace(/[^a-zA-Z0-9]/g, '')

        // 비교 후 출력
        if (cleanedResponse === cleanedSecondTdValue) {
          await markSuccess()
        } else {
          outputTarget.textContent = "실패"
          alert(`응답이 일치하지 않습니다.\n\n응답: ${cleanedResponse}\n기대한 값: ${cleanedSecondTdValue}`)
        }
      }

    } catch (error) {
      console.log("프린팅 중 오류가 발생했습니다:", error)
      const errorMessage = error?.message || "알 수 없는 오류가 발생했습니다."
      alert(`프린팅 실패:\n\n${errorMessage}`)
      throw error // 에러를 다시 던져서 상위에서 처리하도록 함
    }
  }

  async printSelected(event) {
    event.preventDefault()

    this.togglePrintButtons(false)

    const dataset = event.currentTarget.dataset

    if (dataset.printerEpc) {
      // 개별 출력 버튼이 클릭된 경우
      const index = dataset.printerIndex
      const epc = dataset.printerEpc
      const itemName = dataset.printerItemName
      const nickName = dataset.printerNickName
      const basePrice = dataset.printerBasePrice
      const repotPrice = dataset.printerRepotPrice || ""
      const itemCode = dataset.printerItemCode
      const serial = dataset.printerSerial

      console.log(`개별 출력: ${index}, ${epc}, ${itemName}, ${nickName}, ${basePrice}, ${repotPrice}, ${itemCode}, ${serial}`)

      try {
        // 포트 열기
        this.port = await this.openPort()
        await this.print({
          currentTarget: {
            dataset: {
              printerIndex: index,
              printerEpc: epc,
              printerItemName: itemName,
              printerNickName: nickName,
              printerBasePrice: basePrice,
              printerRepotPrice: repotPrice,
              printerItemCode: itemCode,
              printerSerial: serial,
            },
          },
        })
      } catch (error) {
        alert(`개별 출력 실패:\n\n${error.message}`)
      } finally {
        // 포트 닫기
        await this.closePort()
        this.togglePrintButtons(true)
      }
      return
    }

    const checkedItems = this.itemTargets.filter((checkbox) => checkbox.checked)

    if (checkedItems.length === 0) {
      this.togglePrintButtons(true)
      alert("선택된 항목이 없습니다.")
      return
    }

    try {
      // 포트를 한 번만 열기
      this.port = await this.openPort()

      const printItem = async (itemIndex) => {
        if (itemIndex >= checkedItems.length) {
          // 포트 닫기
          await this.closePort()

          await new Promise((resolve) => setTimeout(resolve, 1000))

          this.togglePrintButtons(true)

          alert("출력 완료")

          return
        }

        const row = checkedItems[itemIndex].closest("tr")

        const index = row.dataset.printerIndex
        const epc = row.dataset.epc
        const itemName = row.dataset.itemName
        const nickName = row.dataset.nickName
        const basePrice = row.dataset.basePrice
        const repotPrice = row.dataset.repotPrice || ""
        const itemCode = row.dataset.itemCode
        const serial = row.dataset.serial

        console.log(`출력 중: ${index}, ${epc}, ${itemName}, ${nickName}, ${basePrice}, ${repotPrice}, ${itemCode}, ${serial}`)

        try {
          await this.print({
            currentTarget: {
              dataset: {
                printerIndex: index,
                printerEpc: epc,
                printerItemName: itemName,
                printerNickName: nickName,
                printerBasePrice: basePrice,
                printerRepotPrice: repotPrice,
                printerItemCode: itemCode,
                printerSerial: serial,
              },
            },
          })
          await new Promise((resolve) => setTimeout(resolve, 0))
          await printItem(itemIndex + 1)
        } catch (error) {
          console.log(`프린팅 실패: ${error.message}`)
          throw error
        }
      }

      await printItem(0)
    } catch (error) {
      alert(`작업이 중단되었습니다:\n\n${error.message}`)

      this.togglePrintButtons(true)

      // 에러 발생 시 포트 닫기
      await this.closePort()
    }
  }
}
